import React, { useState, useEffect, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { formatCoordinates, FormattedCoordinates } from '../../../utils/coordinateFormatters';
import './CoordinateDisplay.scss';

interface CoordinateDisplayProps {
  map: mapboxgl.Map | null;
}

const CoordinateDisplay: React.FC<CoordinateDisplayProps> = ({ map }) => {
  const [coords, setCoords] = useState<FormattedCoordinates | null>(null);
  const isHovering = React.useRef(false);

  const handleMouseMove = useCallback((e: mapboxgl.MapMouseEvent) => {
    const { lat, lng } = e.lngLat;
    setCoords(formatCoordinates(lat, lng));
  }, []);

  const handleMouseLeave = useCallback(() => {
    if (!isHovering.current) {
      setCoords(null);
    }
  }, []);

  useEffect(() => {
    if (!map) return;

    map.on('mousemove', handleMouseMove);
    map.on('mouseout', handleMouseLeave);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('mouseout', handleMouseLeave);
    };
  }, [map, handleMouseMove, handleMouseLeave]);

  if (!coords) {
    return (
      <div className="coordinate-display coordinate-display--empty">
        <span className="coordinate-display__placeholder">Move cursor over map</span>
      </div>
    );
  }

  // Stop events from bubbling to map
  const stopPropagation = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  return (
    <div
      className="coordinate-display"
      onMouseEnter={() => { isHovering.current = true; }}
      onMouseLeave={() => { isHovering.current = false; setCoords(null); }}
      onMouseDown={stopPropagation}
      onMouseUp={stopPropagation}
      onClick={stopPropagation}
      onDoubleClick={stopPropagation}
      onContextMenu={stopPropagation}
    >
      <div className="coordinate-display__row">
        <span className="coordinate-display__label">DDM:</span>
        <span className="coordinate-display__value">{coords.dmm}</span>
      </div>
      <div className="coordinate-display__row">
        <span className="coordinate-display__label">DD:</span>
        <span className="coordinate-display__value">{coords.dd}</span>
      </div>
      <div className="coordinate-display__row">
        <span className="coordinate-display__label">DMS:</span>
        <span className="coordinate-display__value">{coords.dms}</span>
      </div>
    </div>
  );
};

export default CoordinateDisplay;
